// src/components/Recognitions.tsx
import React from "react";
import { useRecognitionsAPI } from "@/hooks/useRecognitionsAPI";

export const Recognitions: React.FC = () => {
  const {
    categories,
    items,
    activeCategoryKey,
    setActiveCategoryKey,
    loading,
    error,
    // Optional: these exist on the hook we shipped
    manifestUrl,
    updatedAt,
  } = useRecognitionsAPI();

  return (
    <section id="recognitions" className="py-16">
      <div className="container max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-serif text-center mb-8">
          Recognitions
        </h2>

        {/* Tabs */}
        <div className="mb-16 md:mb-12 bg-transparent">
          <div
            className="grid w-full max-w-4xl mx-auto grid-cols-3 lg:grid-cols-6 gap-2 bg-transparent p-0 pb-2"
          >
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveCategoryKey(c.key)}
                type="button"
                className={`px-3 py-3 rounded-full border text-sm transition font-medium whitespace-nowrap overflow-hidden text-ellipsis ${
                  activeCategoryKey === c.key
                    ? "bg-rose-200/60 border-rose-300 text-rose-900"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
                aria-pressed={activeCategoryKey === c.key}
                aria-label={`${c.label} (${c.count})`}
              >
                <span className="block truncate">{c.label}</span>
                <span className="ml-2 text-xs opacity-70">({c.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* States & Cards */}
        <div className="mt-6 md:mt-0">
          {loading && (
            <p className="text-center text-sm text-muted-foreground">Loading…</p>
          )}
          {error && (
            <p className="text-center text-sm text-red-600">Error: {error}</p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              No recognitions to show yet.
            </p>
          )}

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((card) => {
            const hasImg = !!card.imageUrl;
            const title = card.title ?? "";
            const desc = card.description ?? "";
            const l1 = card?.links?.[0];
            const l2 = card?.links?.[1];

            return (
              <article
                key={card.id} // ← stable, present for every item
                className="rounded-xl overflow-hidden border bg-white shadow-sm"
              >
                <div className={`aspect-[16/10] ${hasImg ? "bg-gray-100" : "bg-gray-50"}`}>
                  {hasImg ? (
                    <img
                      src={card.imageUrl}
                      alt={title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No image
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold mb-1">{title}</h3>
                  {desc && (
                    <p className="text-sm text-muted-foreground mb-3">{desc}</p>
                  )}

                  <div className="flex gap-3 text-sm">
                    {l1?.href && (
                      <a
                        className="underline underline-offset-2 hover:opacity-80"
                        href={l1.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {l1.label ?? "Link"}
                      </a>
                    )}
                    {l2?.href && (
                      <a
                        className="underline underline-offset-2 hover:opacity-80"
                        href={l2.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {l2.label ?? "Link"}
                      </a>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          </div>
        </div>

        {/* Debug (only shows in dev) */}
        {import.meta.env.DEV && (
          <div className="mt-10 text-center text-xs text-gray-500 space-y-1">
            <div>
              Data source:{" "}
              <code>{manifestUrl || import.meta.env.VITE_RECOGNITIONS_MANIFEST_URL || "Not set"}</code>
            </div>
            {updatedAt && (
              <div>
                Updated: <code>{updatedAt}</code>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Recognitions;
